import { get_member_attendance_summary, download_member_pdf } from "services/api-service";
import { REACT_APP_API_URL } from "../../config/api-config";
import React, { useState, useEffect, useCallback } from "react";

function AsyncImage({ src, alt, style, onClick }) {
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div
      onClick={onClick}
      style={{
        position: "relative",
        width: style.width || "100%",
        maxWidth: style.maxWidth || "130px",
        height: style.height || "130px",
        display: "inline-block",
        cursor: onClick ? "pointer" : "default"
      }}
    >
      {!hasLoaded && !error && (
        <div
          style={{
            position: "absolute", top: 0, left: 0, width: "100%", height: "100%", borderRadius: style.borderRadius || "6px",
            backgroundColor: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center",
            color: "#6b7280", fontSize: "11px", fontWeight: "500", border: "1px dashed #d1d5db", zIndex: 1,
          }}
        >
          Loading...
        </div>
      )}

      {error && (
        <div
          style={{
            position: "absolute", top: 0, left: 0, width: "100%", height: "100%", borderRadius: style.borderRadius || "6px",
            backgroundColor: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center",
            color: "#ef4444", fontSize: "11px", fontWeight: "500", border: "1px solid #fca5a5", zIndex: 1,
          }}
        >
          No Image
        </div>
      )}

      <img
        src={src}
        alt={alt}
        loading="lazy"
        style={{
          ...style,
          opacity: hasLoaded ? 1 : 0,
          transition: "opacity 0.2s ease-in-out",
        }}
        onLoad={() => setHasLoaded(true)}
        onError={() => setError(true)}
      />
    </div>
  );
}

export default function MemberAttendanceTable() {
  const today = new Date();
  const formattedToday = today.toISOString().split("T")[0];

  const [activeView, setActiveView] = useState("daily");

  const [selectedDate, setSelectedDate] = useState(formattedToday);
  const [visitors, setVisitors] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [expandedRow, setExpandedRow] = useState(null);
  const [enlargedImage, setEnlargedImage] = useState(null); // IMAGE MODAL STATE

  // Filters
  const [selectedId, setSelectedId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedWhomToMeet, setSelectedWhomToMeet] = useState("");
  const [selectedReasonToMeet, setSelectedReasonToMeet] = useState("");

  // Range Report
  const [rangeStartDate, setRangeStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split("T")[0];
  });
  const [rangeEndDate, setRangeEndDate] = useState(formattedToday);
  const [rangeData, setRangeData] = useState([]);
  const [rangeLoading, setRangeLoading] = useState(false);
  const [rangeSelectedWhomToMeet, setRangeSelectedWhomToMeet] = useState("");
  const [rangeSelectedVisitorName, setRangeSelectedVisitorName] = useState("");

  const fetchDataByDate = useCallback(async () => {
    if (!selectedDate) return;
    try {
      setLoading(true);
      const payload = {
        date: selectedDate,
        page_number: 1,
        page_size: 5000, 
      };

      const response = await get_member_attendance_summary(payload);
      setVisitors(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
      setVisitors([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const fetchDateRangeReport = useCallback(async () => {
    if (!rangeStartDate || !rangeEndDate) return;
    try {
      setRangeLoading(true);
      const start = new Date(rangeStartDate);
      const end = new Date(rangeEndDate);
      const allDates = [];
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        allDates.push(d.toISOString().split("T")[0]);
      }

      const allData = [];
      for (const date of allDates) {
        try {
          const payload = {
            date: date,
            page_number: 1,
            page_size: 5000, 
          };
          const response = await get_member_attendance_summary(payload);
          allData.push(...(response?.data?.data || []));
        } catch (err) {}
      }
      setRangeData(allData);
    } catch (error) {
      console.error("Error fetching range:", error);
      setRangeData([]);
    } finally {
      setRangeLoading(false);
    }
  }, [rangeStartDate, rangeEndDate]);

  useEffect(() => {
    if (activeView === "daily") fetchDataByDate();
  }, [activeView, fetchDataByDate]);

  const filteredVisitors = visitors.filter((c) => {
    const matchesId = !selectedId || String(c.member_id) === String(selectedId);
    const matchesStatus = selectedStatus === "all" || c.status === selectedStatus;
    const matchesWhom = !selectedWhomToMeet || c.buying_behaviour === selectedWhomToMeet;
    const matchesReason = !selectedReasonToMeet || c.pricing === selectedReasonToMeet;
    
    return matchesId && matchesStatus && matchesWhom && matchesReason;
  });

  const uniqueVisitorIds = [...new Set(visitors.map((c) => c.member_id))];
  const uniqueWhomToMeet = [...new Set(visitors.map((c) => c.buying_behaviour).filter(Boolean))].sort();
  const uniqueReasons = [...new Set(visitors.map((c) => c.pricing).filter(Boolean))].sort();

  const processRangeData = () => {
    const reportMap = {};
    rangeData.forEach((record) => {
      const id = record.member_id;
      if (!reportMap[id]) {
        reportMap[id] = {
          visitor_id: id,
          visitor_name: record.member_name,
          whom_to_meet: record.buying_behaviour,
          reason_to_meet: record.pricing,
          total_visits: 0,
          total_hours: 0,
        };
      }
      if (record.status === "Present") {
        reportMap[id].total_visits += 1;
        reportMap[id].total_hours += record.total_visit_hours || 0;
      }
    });

    Object.values(reportMap).forEach((record) => {
      record.avg_duration = record.total_visits > 0 ? (record.total_hours / record.total_visits).toFixed(2) : 0;
    });

    let filtered = Object.values(reportMap);
    if (rangeSelectedWhomToMeet) filtered = filtered.filter(r => r.whom_to_meet === rangeSelectedWhomToMeet);
    if (rangeSelectedVisitorName) filtered = filtered.filter(r => r.visitor_name.toLowerCase().includes(rangeSelectedVisitorName.toLowerCase()));

    return filtered.sort((a, b) => b.total_visits - a.total_visits);
  };

  const rangeReport = processRangeData();
  const rangeUniqueWhomToMeet = [...new Set(rangeData.map((c) => c.buying_behaviour).filter(Boolean))].sort();
  const rangeUniqueNames = [...new Set(rangeData.map((c) => c.member_name).filter(Boolean))].sort();

  const handleDownloadPDF = async () => {
    try {
      const payload = {
        date: selectedDate,
        member_name: selectedId || undefined,
        status: selectedStatus !== "all" ? selectedStatus : undefined,
        buying_behaviour: selectedWhomToMeet || undefined,
        pricing: selectedReasonToMeet || undefined
      };
      
      const response = await download_member_pdf(payload);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `visitor_log_${selectedDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF Download error:", error);
      alert("Failed to download PDF. No records matched your current filters.");
    }
  };

  const presentCount = visitors.filter(c => c.status === "Present").length;
  const totalHours = filteredVisitors.reduce((sum, c) => sum + (c.total_visit_hours || 0), 0).toFixed(2);
  const totalVisitsInRange = rangeReport.reduce((sum, r) => sum + r.total_visits, 0);

  return (
    <div style={{ backgroundColor: "#f8f9fa", minHeight: "100vh", padding: "24px", fontFamily: "'Inter', sans-serif" }}>
      <div style={{ marginBottom: "24px" }}>
        
        <div style={{ display: "flex", gap: "8px", marginBottom: "24px", borderBottom: "2px solid #e5e7eb" }}>
          <button onClick={() => setActiveView("daily")} style={{ padding: "12px 24px", backgroundColor: "transparent", color: activeView === "daily" ? "#4f46e5" : "#6b7280", border: "none", borderBottom: activeView === "daily" ? "3px solid #4f46e5" : "none", cursor: "pointer", fontWeight: "600", fontSize: "15px" }}>📅 Daily Walk-ins</button>
          <button onClick={() => setActiveView("range")} style={{ padding: "12px 24px", backgroundColor: "transparent", color: activeView === "range" ? "#4f46e5" : "#6b7280", border: "none", borderBottom: activeView === "range" ? "3px solid #4f46e5" : "none", cursor: "pointer", fontWeight: "600", fontSize: "15px" }}>📊 Frequency Report</button>
        </div>

        {activeView === "daily" && (
          <>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "16px" }}>
              <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", backgroundColor: "white" }} />
              <button onClick={fetchDataByDate} style={{ padding: "10px 20px", backgroundColor: "#4f46e5", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500", fontSize: "14px" }}>🔍 Search</button>
            </div>

            {visitors.length > 0 && (
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
                <select value={selectedId} onChange={(e) => setSelectedId(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", minWidth: "150px" }}>
                  <option value="">All Visitors</option>
                  {uniqueVisitorIds.map((id) => (
                    <option key={id} value={id}>{visitors.find(m => m.member_id === id)?.member_name} ({id})</option>
                  ))}
                </select>

                <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", minWidth: "140px" }}>
                  <option value="all">All Status</option>
                  <option value="Present">Present</option>
                  <option value="Absent">Absent</option>
                </select>

                <select value={selectedWhomToMeet} onChange={(e) => setSelectedWhomToMeet(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", minWidth: "140px" }}>
                  <option value="">All Hosts (Staff)</option>
                  {uniqueWhomToMeet.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>

                <select value={selectedReasonToMeet} onChange={(e) => setSelectedReasonToMeet(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0", fontSize: "14px", minWidth: "140px" }}>
                  <option value="">All Reasons</option>
                  {uniqueReasons.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>

                <button onClick={() => { setSelectedId(""); setSelectedStatus("all"); setSelectedWhomToMeet(""); setSelectedReasonToMeet(""); }} style={{ padding: "10px 16px", backgroundColor: "#6b7280", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500" }}>↺ Reset</button>
              </div>
            )}
          </>
        )}

        {activeView === "range" && (
          <>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "16px" }}>
              <input type="date" value={rangeStartDate} onChange={(e) => setRangeStartDate(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0" }} />
              <input type="date" value={rangeEndDate} onChange={(e) => setRangeEndDate(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0" }} />
              <button onClick={fetchDateRangeReport} style={{ padding: "10px 20px", backgroundColor: "#4f46e5", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "500" }}>📊 Generate Report</button>
            </div>

            {rangeData.length > 0 && (
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center", marginBottom: "16px" }}>
                <select value={rangeSelectedWhomToMeet} onChange={(e) => setRangeSelectedWhomToMeet(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0" }}>
                  <option value="">All Hosts</option>
                  {rangeUniqueWhomToMeet.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
                <select value={rangeSelectedVisitorName} onChange={(e) => setRangeSelectedVisitorName(e.target.value)} style={{ padding: "10px 12px", borderRadius: "6px", border: "1px solid #d0d0d0" }}>
                  <option value="">All Visitors</option>
                  {rangeUniqueNames.map(n => <option key={n} value={n}>👤 {n}</option>)}
                </select>
              </div>
            )}
          </>
        )}
      </div>

      {activeView === "daily" && (
        <>
          {!loading && filteredVisitors.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px", marginBottom: "24px" }}>
              <div style={{ backgroundColor: "white", padding: "16px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", borderLeft: "4px solid #10b981" }}>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "6px", fontWeight: "600", textTransform: "uppercase" }}>Total Walk-ins</div>
                <div style={{ fontSize: "28px", fontWeight: "700", color: "#10b981" }}>{presentCount}</div>
              </div>
              <div style={{ backgroundColor: "white", padding: "16px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", borderLeft: "4px solid #f59e0b" }}>
                <div style={{ fontSize: "12px", color: "#666", marginBottom: "6px", fontWeight: "600", textTransform: "uppercase" }}>Total Time (Hours)</div>
                <div style={{ fontSize: "28px", fontWeight: "700", color: "#f59e0b" }}>{totalHours}</div>
              </div>
            </div>
          )}

          {!loading && filteredVisitors.length > 0 && (
            <div style={{ backgroundColor: "white", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden", marginBottom: "20px" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Visitor ID</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Name</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Check-In</th>
                    <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Check-Out</th>
                    <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Duration</th>
                    <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Status</th>
                    <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Whom to Meet</th>
                    <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Reason to Meet</th>
                    <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase", letterSpacing: "0.5px" }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVisitors.map((c, idx) => (
                    <React.Fragment key={c.member_id}>
                      <tr style={{ borderBottom: "1px solid #e5e7eb", backgroundColor: idx % 2 === 0 ? "#ffffff" : "#f8fafc", opacity: c.status === "Absent" ? 0.7 : 1 }}>
                        <td style={{ padding: "12px 16px", fontSize: "14px", fontWeight: "700", color: "#0f172a" }}>{c.member_id}</td>
                        <td style={{ padding: "12px 16px", fontSize: "14px", color: "#0f172a" }}>{c.member_name}</td>
                        <td style={{ padding: "12px 16px", fontSize: "14px", color: c.check_in_time ? "#0f172a" : "#94a3b8", fontWeight: "600" }}>{c.check_in_time ? c.check_in_time.substring(0, 5) : "—"}</td>
                        <td style={{ padding: "12px 16px", fontSize: "14px", color: c.check_out_time ? "#0f172a" : "#94a3b8", fontWeight: "600" }}>{c.check_out_time ? c.check_out_time.substring(0, 5) : "—"}</td>
                        <td style={{ padding: "12px 16px", fontSize: "14px", color: "#1e40af", fontWeight: "700", textAlign: "center" }}>{c.total_visit_hours > 0 ? `${c.total_visit_hours.toFixed(2)}h` : "—"}</td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          <span style={{ display: "inline-block", padding: "6px 12px", borderRadius: "6px", fontSize: "12px", fontWeight: "700", backgroundColor: c.status === "Present" ? "#d1fae5" : "#ffe4e6", color: c.status === "Present" ? "#047857" : "#be123c" }}>
                            {c.status}
                          </span>
                        </td>
                        <td style={{ padding: "12px 16px", textAlign: "center", color: "#d97706", fontWeight: "600", fontSize: "12px" }}>{c.buying_behaviour || "—"}</td>
                        <td style={{ padding: "12px 16px", textAlign: "center", color: "#059669", fontWeight: "600", fontSize: "12px" }}>{c.pricing || "—"}</td>
                        <td style={{ padding: "12px 16px", textAlign: "center" }}>
                          <button onClick={() => setExpandedRow(expandedRow === c.member_id ? null : c.member_id)} style={{ padding: "6px 14px", backgroundColor: "white", color: "#1e40af", border: "1px solid #1e40af", borderRadius: "6px", cursor: "pointer", fontSize: "12px", fontWeight: "700", transition: "all 0.2s" }} onMouseOver={(e) => { e.currentTarget.style.backgroundColor = "#1e40af"; e.currentTarget.style.color = "white"; }} onMouseOut={(e) => { e.currentTarget.style.backgroundColor = "white"; e.currentTarget.style.color = "#1e40af"; }}>
                            {expandedRow === c.member_id ? "Close" : "View"}
                          </button>
                        </td>
                      </tr>
                      {expandedRow === c.member_id && (
                        <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                          <td colSpan="9" style={{ padding: "24px" }}>
                            <div style={{ display: "flex", gap: "24px", justifyContent: "center" }}>
                              {c.detected_face && (
                                <div style={{ textAlign: "center", backgroundColor: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                                  <div style={{ marginBottom: "12px", fontWeight: "700", color: "#1e40af", fontSize: "12px", textTransform: "uppercase" }}>Latest Detection</div>
                                  <AsyncImage 
                                    src={`${REACT_APP_API_URL}${c.detected_face}`} 
                                    alt="Detected" 
                                    style={{ width: "120px", height: "120px", borderRadius: "8px", objectFit: "cover", border: "2px solid #1e40af", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }} 
                                    onClick={() => setEnlargedImage(`${REACT_APP_API_URL}${c.detected_face}`)}
                                  />
                                </div>
                              )}
                              {c.original_face && (
                                <div style={{ textAlign: "center", backgroundColor: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                                  <div style={{ marginBottom: "12px", fontWeight: "700", color: "#475569", fontSize: "12px", textTransform: "uppercase" }}>Registered Identity</div>
                                  <AsyncImage 
                                    src={`${REACT_APP_API_URL}${c.original_face}`} 
                                    alt="Registered" 
                                    style={{ width: "120px", height: "120px", borderRadius: "8px", objectFit: "cover", border: "1px solid #cbd5e1", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }} 
                                    onClick={() => setEnlargedImage(`${REACT_APP_API_URL}${c.original_face}`)}
                                  />
                                </div>
                              )}
                              <div style={{ backgroundColor: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e8f0", minWidth: "200px" }}>
                                <div style={{ marginBottom: "12px", fontWeight: "700", color: "#0f172a", fontSize: "12px", textTransform: "uppercase", borderBottom: "1px solid #e2e8f0", paddingBottom: "8px" }}>Metrics</div>
                                <div style={{ fontSize: "13px", lineHeight: "2.2", color: "#334155" }}>
                                  <div style={{ display: "flex", justifyContent: "space-between" }}><strong style={{ color: "#64748b" }}>Gender:</strong> <span>{c.gender || "—"}</span></div>
                                  <div style={{ display: "flex", justifyContent: "space-between" }}><strong style={{ color: "#64748b" }}>Visitor Type:</strong> <span>{c.currency || "Visitor"}</span></div>
                                  <div style={{ display: "flex", justifyContent: "space-between" }}><strong style={{ color: "#64748b" }}>Entry Point:</strong> <span style={{ fontWeight: "600", color: "#1e40af" }}>{c.branch_location || "—"}</span></div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          
          {/* Empty State Fallback */}
          {!loading && filteredVisitors.length === 0 && visitors.length > 0 && (
            <div style={{ backgroundColor: "white", padding: "40px", textAlign: "center", borderRadius: "10px", color: "#94a3b8", marginTop: "20px", border: "1px dashed #cbd5e1", fontWeight: "500" }}>
              No records match the selected filters.
            </div>
          )}

          {!loading && filteredVisitors.length > 0 && (
            <button onClick={handleDownloadPDF} style={{ padding: "10px 20px", backgroundColor: "#1e40af", color: "white", border: "none", borderRadius: "6px", cursor: "pointer", fontWeight: "600", fontSize: "14px", marginTop: "12px" }}>📄 Download PDF</button>
          )}
        </>
      )}

      {activeView === "range" && (
        <>
          {!rangeLoading && rangeReport.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "12px", marginBottom: "24px" }}>
              <div style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", borderLeft: "4px solid #0f172a" }}>
                <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "6px", fontWeight: "600", textTransform: "uppercase" }}>Unique Visitors</div>
                <div style={{ fontSize: "32px", fontWeight: "800", color: "#0f172a" }}>{rangeReport.length}</div>
              </div>
              <div style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", borderLeft: "4px solid #1e40af" }}>
                <div style={{ fontSize: "12px", color: "#64748b", marginBottom: "6px", fontWeight: "600", textTransform: "uppercase" }}>Total Recorded Visits</div>
                <div style={{ fontSize: "32px", fontWeight: "800", color: "#1e40af" }}>{totalVisitsInRange}</div>
              </div>
            </div>
          )}

          {!rangeLoading && rangeReport.length > 0 && (
            <div style={{ backgroundColor: "white", borderRadius: "10px", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)", overflow: "hidden", marginBottom: "20px", border: "1px solid #e2e8f0" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                    <th style={{ padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase" }}>ID</th>
                    <th style={{ padding: "16px 24px", textAlign: "left", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase" }}>Name</th>
                    <th style={{ padding: "16px 24px", textAlign: "center", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase" }}>Total Visits</th>
                    <th style={{ padding: "16px 24px", textAlign: "center", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase" }}>Avg Duration</th>
                    <th style={{ padding: "16px 24px", textAlign: "center", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase" }}>Whom to Meet</th>
                    <th style={{ padding: "16px 24px", textAlign: "center", fontSize: "12px", fontWeight: "700", color: "#475569", textTransform: "uppercase" }}>Reason to Meet</th>
                  </tr>
                </thead>
                <tbody>
                  {rangeReport.map((r, idx) => (
                    <tr key={r.visitor_id} style={{ borderBottom: "1px solid #e2e8f0", backgroundColor: "#ffffff" }}>
                      <td style={{ padding: "16px 24px", fontSize: "14px", fontWeight: "700", color: "#0f172a" }}>{r.visitor_id}</td>
                      <td style={{ padding: "16px 24px", fontSize: "14px", color: "#0f172a" }}>{r.visitor_name}</td>
                      <td style={{ padding: "16px 24px", textAlign: "center" }}><span style={{ display: "inline-block", padding: "4px 10px", borderRadius: "6px", backgroundColor: "#d1fae5", color: "#047857", fontWeight: "700" }}>{r.total_visits}</span></td>
                      <td style={{ padding: "16px 24px", fontSize: "14px", color: "#1e40af", fontWeight: "700", textAlign: "center" }}>{r.avg_duration}h</td>
                      <td style={{ padding: "16px 24px", fontSize: "13px", color: "#d97706", textAlign: "center", fontWeight: "600" }}>{r.whom_to_meet || "—"}</td>
                      <td style={{ padding: "16px 24px", fontSize: "13px", color: "#059669", textAlign: "center", fontWeight: "600" }}>{r.reason_to_meet || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* FULL-SCREEN IMAGE MODAL */}
      {enlargedImage && (
        <div 
          onClick={() => setEnlargedImage(null)} 
          style={{ 
            position: "fixed", top: 0, left: 0, width: "100%", height: "100%", 
            background: "rgba(15, 23, 42, 0.9)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center", 
            zIndex: 99999, cursor: "pointer" 
          }}
        >
          <img 
            src={enlargedImage} 
            alt="Enlarged Detail" 
            style={{ maxWidth: "90%", maxHeight: "90%", borderRadius: "12px", objectFit: "contain", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)", border: "4px solid white" }} 
            onClick={(e) => e.stopPropagation()} 
          />
          <button 
            onClick={() => setEnlargedImage(null)} 
            style={{ position: "absolute", top: "24px", right: "24px", background: "white", border: "none", borderRadius: "50%", width: "40px", height: "40px", fontSize: "20px", fontWeight: "bold", color: "#0f172a", cursor: "pointer", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}
